import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

import { CONTAINER, EYEBROW, EYEBROW_MARK, H2, LEAD, MOTION_TOGGLE } from '../classes';
import { CountFigure, useCountProgress } from '../count-up';
import { cx, IMG, m, prefersReducedMotion } from '../shared';
import { PlayPauseIcons } from './proof-clips';

interface Hub {
  id: 'us' | 'eu' | 'au';
  delay: string;
  origin: [number, number];
  targets: Array<{ to: [number, number]; ctrl: [number, number] }>;
}

// Routes hand over US → Europe → Australia, 7 s each (21 s cycle; the delays offset the three hubs).
const HUBS: Hub[] = [
  {
    id: 'us',
    delay: '0s',
    origin: [310, 261],
    targets: [
      { ctrl: [223, 244], to: [148, 291] },
      { ctrl: [398, 374], to: [384, 517] },
      { ctrl: [436, 208], to: [564, 257] },
      { ctrl: [527, 312], to: [650, 498] },
    ],
  },
  {
    id: 'eu',
    delay: '-14s',
    origin: [564, 257],
    targets: [
      { ctrl: [436, 208], to: [310, 261] },
      { ctrl: [655, 360], to: [650, 498] },
      { ctrl: [657, 270], to: [716, 344] },
      { ctrl: [777, 287], to: [910, 456] },
    ],
  },
  {
    id: 'au',
    delay: '-7s',
    origin: [1037, 581],
    targets: [
      { ctrl: [988, 548], to: [929, 558] },
      { ctrl: [999, 493], to: [910, 456] },
      { ctrl: [958, 425], to: [793, 367] },
      { ctrl: [1064, 431], to: [980, 304] },
    ],
  },
];
const COMET_DELAYS = ['0s', '1.15s', '2.3s', '3.45s'];
const PULSE_DELAYS = ['0.8s', '1.6s', '2.4s', '3.2s'];

const withDelay = (name: string, value: string) => ({ [name]: value }) as CSSProperties;

function HubRoutes({ hub }: { hub: Hub }) {
  const paths = hub.targets.map((target) => `M${hub.origin[0]} ${hub.origin[1]} Q${target.ctrl[0]} ${target.ctrl[1]} ${target.to[0]} ${target.to[1]}`);
  return (
    <g className={m(`hub hub--${hub.id}`)} style={withDelay('--hd', hub.delay)}>
      {paths.map((path) => (
        <path key={`h${path}`} className={m('rt__h')} d={path} />
      ))}
      {paths.map((path) => (
        <path key={`t${path}`} className={m('rt__t')} pathLength={100} d={path} />
      ))}
      {paths.map((path, index) => (
        <path key={`c${path}`} className={m('rt__c')} style={withDelay('--d', COMET_DELAYS[index] ?? '0s')} pathLength={100} d={path} />
      ))}
      <circle className={m('nd__p')} style={withDelay('--d', '0s')} cx={hub.origin[0]} cy={hub.origin[1]} r='7' />
      {hub.targets.map((target, index) => (
        <circle key={`p${target.to.join()}`} className={m('nd__p')} style={withDelay('--d', PULSE_DELAYS[index] ?? '0s')} cx={target.to[0]} cy={target.to[1]} r='6' />
      ))}
      <circle className={m('nd__d nd__d--hub')} cx={hub.origin[0]} cy={hub.origin[1]} r='3.6' />
      {hub.targets.map((target) => (
        <circle key={`d${target.to.join()}`} className={m('nd__d')} cx={target.to[0]} cy={target.to[1]} r='3' />
      ))}
    </g>
  );
}

const STAT_ITEM = 'flex min-w-0 flex-col gap-[.15rem] bg-[linear-gradient(180deg,rgb(14_20_15/.95),rgb(9_13_10/.95))] p-[clamp(1rem,.7rem+1vw,1.5rem)] forced-colors:border forced-colors:border-[CanvasText] lg:py-[var(--st-pad)]';
const STAT_NUMBER = 'whitespace-nowrap text-[min(clamp(1.5rem,1.05rem+1.6vw,2.25rem),8.5vw)] font-bold tabular-nums leading-[1.05] tracking-[-.035em] text-white min-[896px]:text-[min(clamp(1.5rem,1.05rem+1.6vw,2.25rem),4.6vw)] lg:text-[length:var(--st-n)]';
const STAT_LABEL = 'text-[.875rem] font-semibold leading-[1.4] text-[#93FF18] forced-colors:text-[LinkText]';
const STAT_SUB = 'text-[.75rem] leading-[1.4] text-[#9AA3B5]';

export function Servers() {
  const { t } = useTranslation('pages/trial-matrix');
  const { locale } = useRouter();
  const frameRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [offScreen, setOffScreen] = useState(false);
  const [statsRef, progress] = useCountProgress<HTMLUListElement>(1600);

  // reduced motion: the map rests on the US hub, and its control can still resume it
  useEffect(() => {
    if (prefersReducedMotion()) setPaused(true);
    const frame = frameRef.current;
    if (!frame || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => setOffScreen(!entries[0]?.isIntersecting), { rootMargin: '100px 0px' });
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const figure = (value: string) => (
    <CountFigure progress={progress} locale={locale} grouped className='tabular-nums' valueClassName='justify-self-end'>
      {value}
    </CountFigure>
  );

  return (
    <section className={`${m('sec--net')} relative scroll-mt-6 bg-[linear-gradient(180deg,#070A08,#060907_40%,#070A08)] py-[clamp(3rem,1.5rem+6vw,6rem)] lg:scroll-mt-0 lg:py-[var(--net-pad)]`} id='servidores' aria-labelledby='srv-t'>
      <div className={`${CONTAINER} ${m('net-grid')} lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center lg:gap-x-[var(--net-cgap)] lg:gap-y-[var(--net-gap)] lg:[grid-template-areas:'head_net'_'stats_stats']`}>
        <div className='mb-[clamp(1.75rem,1rem+2.4vw,3rem)] max-w-[46rem] lg:m-0 lg:max-w-[34rem] lg:[grid-area:head]' data-reveal=''>
          <p className={EYEBROW}>
            <span className={EYEBROW_MARK} aria-hidden='true'>
              &gt;
            </span>{' '}
            {t('lp.servers.eyebrow')}
          </p>
          <h2 className={`${H2} ${m('net-title')}`} id='srv-t'>
            <Trans t={t} i18nKey='lp.servers.title' components={{ em: <em /> }} />
          </h2>
          <p className={LEAD}>{t('lp.servers.lead')}</p>
        </div>

        <div className={`${m('net-card')} isolate overflow-hidden rounded-3xl border border-[#2C3B31] bg-[linear-gradient(180deg,rgb(12_18_13/.9),rgb(7_11_8/.95))] shadow-[var(--shadow-lg)] lg:[grid-area:net] forced-colors:border-[CanvasText]`} data-reveal=''>
          <div ref={frameRef} className={`${m(cx('net__frame', paused && 'is-paused', offScreen && 'is-off'))} relative overflow-hidden`} id='mapa-rede'>
            <img className='block h-auto w-full opacity-[.92]' src={`${IMG}/mapa-rede.svg`} alt={t('lp.servers.mapAlt')} width={1121} height={660} loading='lazy' decoding='async' />
            <svg className='pointer-events-none absolute inset-0 size-full' viewBox='0 0 1121 660' preserveAspectRatio='none' aria-hidden='true' focusable='false'>
              {HUBS.map((hub) => (
                <HubRoutes key={hub.id} hub={hub} />
              ))}
            </svg>

            <span className={m('net__scan')} aria-hidden='true' />
            <span className='absolute start-3 top-3 z-[3] inline-flex items-center gap-[.45rem] rounded-full border border-[#93FF18]/[.22] bg-[rgb(5_8_6/.8)] px-[.7rem] py-[.35rem] font-mono text-[.6875rem] font-semibold uppercase tracking-[.1em] text-[#CBD5E1]'>
              <i className={`${m('net__chip-dot')} size-1.5 rounded-full bg-[#93FF18] shadow-[0_0_8px_rgb(147_255_24/.9)] forced-colors:bg-[Highlight]`} aria-hidden='true' />
              {t('lp.servers.chip')}
              <span className={`${m('net__hubs')} inline-grid text-[#93FF18]`} aria-hidden='true'>
                <span style={withDelay('--hd', '0s')}>{t('lp.servers.hubUs')}</span>
                <span style={withDelay('--hd', '-14s')}>{t('lp.servers.hubEu')}</span>
                <span style={withDelay('--hd', '-7s')}>{t('lp.servers.hubAu')}</span>
              </span>
            </span>
            <button className={`${MOTION_TOGGLE} absolute bottom-3 end-3 z-[3]`} type='button' data-paused={paused ? 'true' : 'false'} aria-controls='mapa-rede' onClick={() => setPaused((value) => !value)}>
              <PlayPauseIcons />
              <span>{t(paused ? 'lp.servers.play' : 'lp.servers.pause')}</span>
            </button>
          </div>
          <ul className='flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-[#2C3B31] px-[clamp(1.15rem,.8rem+1.1vw,1.75rem)] py-[clamp(.85rem,.6rem+.7vw,1.15rem)] font-mono text-[.6875rem] uppercase tracking-[.08em] text-[#9AA3B5] max-md:gap-x-5 max-[480px]:flex-col max-[480px]:items-start max-[480px]:gap-3 lg:min-h-11 lg:py-[.4rem]'>
            <li className='inline-flex min-w-0 items-center gap-[.45rem]'>
              <span className='size-3 flex-none rounded-full bg-[#93FF18] shadow-[0_0_8px_rgb(147_255_24/.7)] forced-colors:bg-[Highlight]' aria-hidden='true' />
              {t('lp.servers.legendPop')}
            </li>
            <li className='inline-flex min-w-0 items-center gap-[.45rem]'>
              <span className='h-0.5 w-5 flex-none bg-[repeating-linear-gradient(90deg,#93FF18_0_4px,transparent_4px_8px)] forced-colors:bg-[Highlight]' aria-hidden='true' />
              {t('lp.servers.legendRoute')}
            </li>
            <li className='ms-auto inline-flex min-w-0 items-center text-[#93FF18] max-md:ms-0 min-[480px]:max-md:basis-full'>{t('lp.servers.legendCoverage')}</li>
          </ul>
        </div>

        <ul ref={statsRef} className='mt-[clamp(1.5rem,1rem+1.6vw,2.25rem)] grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#2C3B31] bg-[#2C3B31] max-[352px]:grid-cols-1 min-[896px]:grid-cols-4 lg:m-0 lg:[grid-area:stats]' data-reveal-group=''>
          <li className={STAT_ITEM}>
            <strong className={STAT_NUMBER}>
              {figure('2000')}
              <span className='text-[#93FF18]'>+</span>
            </strong>
            <span className={STAT_LABEL}>{t('lp.servers.serversLabel')}</span>
            <span className={STAT_SUB}>{t('lp.servers.serversSub')}</span>
          </li>
          <li className={STAT_ITEM}>
            <strong className={STAT_NUMBER}>
              {figure('150')}
              <span className='text-[#93FF18]'>+</span>
            </strong>
            <span className={STAT_LABEL}>{t('lp.servers.countriesLabel')}</span>
            <span className={STAT_SUB}>{t('lp.servers.countriesSub')}</span>
          </li>
          <li className={STAT_ITEM}>
            <strong className={STAT_NUMBER}>
              <Trans t={t} i18nKey='lp.servers.playersValue' components={{ n: <CountFigure progress={progress} locale={locale} grouped className='tabular-nums' valueClassName='justify-self-end' /> }} />
              <span className='text-[#93FF18]'>+</span>
            </strong>
            <span className={STAT_LABEL}>{t('lp.servers.playersLabel')}</span>
            <span className={STAT_SUB}>{t('lp.servers.playersSub')}</span>
          </li>
          <li className={STAT_ITEM}>
            <strong className={STAT_NUMBER}>
              {figure('3000')}
              <span className='text-[#93FF18]'>+</span>
            </strong>
            <span className={STAT_LABEL}>{t('lp.servers.gamesLabel')}</span>
            <span className={STAT_SUB}>{t('lp.servers.gamesSub')}</span>
          </li>
        </ul>
      </div>
    </section>
  );
}
